import { Objective } from './objective.js';
import { ObjectiveGroupConfig } from './objective-group-config.js';
import { ObjectiveConfig } from './objective-config.js';
import { Bonus, AnyPenalty } from './bonus.js';
import { arrayCompare, indexBy } from './util.js';
import { ObjectiveAchievement } from './team-progress.js';

// @typescript-to-gdscript-skip
export interface ObjectivesOrAnswers<C> {
    asObjectives(
        config: C[],
        group?: ObjectiveGroupConfig,
        bonuses?: Bonus[],
        penalties?: AnyPenalty[]
    ): Objective[];
    equals(other: ObjectivesOrAnswers<C>): boolean;
}

// @typescript-to-gdscript-skip
export interface ApiEndpointRequest<T> {
    time: number;
    body: T;
}

// @typescript-to-gdscript-skip
export interface IosPushNotificationData {
    sn?: string;
    rawlog: { time?: string; msg: string };
}

// @typescript-to-gdscript-skip
export interface IosPushNotificationBody {
    data: IosPushNotificationData;
}

export interface NotificationRequestBody {
    timestamp: number;
    message: string;
    sender: string;
    /** Send as a cheating warning: game clients show a modal the player must
     * acknowledge. Ignored for broadcasts (team-targeted only). */
    warning?: boolean;
}

export interface NotificationMessage {
    id: string;
    // @typescript-to-gdscript-type: Iso8601Date
    timestamp: string;
    message: string;
    sender: string;
    silent?: boolean;
    important?: boolean;
    /** Cheating warning flag: game clients show a modal the player must
     * acknowledge before they can continue.
     * @see warnClientIds */
    warning?: boolean;
    /** Root client ids that were connected when the warning was sent. The
     * team counts as "acknowledged" once every one of these clients has the
     * notification id in its deletedNotifications. */
    warnClientIds?: number[];
}
export interface LightHookRequestBody {
    duration: number;
    color: number;
}

export interface LightHookEntry {
    endTime: number;
    color: number;
}

// @typescript-to-gdscript-skip
export type RawObjectiveTypes =
    | Array<ApiEndpointRequest<IosPushNotificationBody | LightHookEntry>>
    | string[]
    | boolean
    | number;

export interface CompletionState {
    completed: number;
    firstCompleted?: number;
}
export type CompletionRecord = Record<string, CompletionState>;
export type TimestampState = Record<string, number>;
interface CompletionTimeState {
    completionTimestamp: number;
    completedDuration: number | null;
}
export type TimeRangeWithCompletionTimeState = Record<string, CompletionTimeState>;
export type BooleanState = Record<string, boolean>;

// @typescript-to-gdscript-skip
export type RawObjectivesState = Record<string, RawObjectiveTypes>;

export class Objectives
    extends Map<string, CompletionState>
    implements ObjectivesOrAnswers<ObjectiveConfig>
{
    constructor(other?: Objectives | Iterable<[string, CompletionState]>) {
        if (other) {
            super(other);
        } else {
            super();
        }
    }

    static statesEqual<Y extends string | number | boolean, T extends Record<string, Y>>(
        a: T,
        b: T
    ) {
        const ak = Object.keys(a);
        const bk = Object.keys(b);
        return ak.length === bk.length && ak.every(k => k in b && b[k] === a[k]);
    }

    static fromState(state: CompletionRecord | TimestampState) {
        return new Objectives(
            Object.keys(state).map(key => <[string, CompletionState]>[key, ensureCr(state[key])])
        );
        function ensureCr(completed: CompletionState | number) {
            if (typeof completed === 'number') {
                return { completed };
            }
            return completed;
        }
    }

    toState() {
        const result: CompletionRecord = {};
        this.forEach((value, key) => (result[key] = value));
        return result;
    }

    score(config: ObjectiveConfig[]): number {
        let result = 0;
        const map = indexBy(config, o => o.id);
        this.forEach((value, key) => (result += (value.completed && map.get(key)?.points) || 0));
        return result;
    }

    completed(): string[] {
        const result = [];
        for (const [k, v] of this.entries()) {
            if (v.completed) {
                result.push(k);
            }
        }
        return result;
    }

    merge(other: Objectives): Objectives {
        const result = new Objectives(this.entries());
        for (const [k, v] of other.entries()) {
            if (v) {
                result.set(k, v);
            }
        }
        return result;
    }

    asObjectives(
        config: ObjectiveConfig[],
        group?: ObjectiveGroupConfig,
        bonuses?: Bonus[],
        penalties?: AnyPenalty[]
    ): Objective[] {
        if (penalties) {
            throw new Error('Objectives cannot have penalties');
        }
        const result = config
            .filter(o => !group || o.group === group.id)
            .map(o => {
                const bonus = bonuses && bonuses.find(b => b.config.name === o.name);
                return new Objective(
                    o.id,
                    this.get(o.id) || { completed: 0 },
                    o,
                    bonus && bonus.index
                );
            });
        const map = indexBy(config, o => o.id);
        // look for objectives for which there is no config and set the value.
        // these will show as checkmarks
        for (const [id, timestamp] of this.entries()) {
            if (!map.get(id) && timestamp) {
                result.push(new Objective(id, timestamp));
            }
        }
        return result;
    }

    equals(other: Objectives) {
        const t = this.completed();
        const o = other.completed();
        return !!t === !!o && t.length === o.length && t.every((a, i) => o[i] === a);
    }

    updateTimestamps(other: ObjectiveAchievement[]) {
        for (const oa of other) {
            this.set(oa.id, oa);
        }
    }
}

export function rawStatesEqual(a: RawObjectivesState, b: RawObjectivesState): boolean {
    const aEntries = Object.entries(a);
    return (
        aEntries.length === Object.keys(b).length &&
        aEntries.every(([id, av]) => rawStateEqual(av, b[id]))
    );
}

export function rawStateEqual(a: RawObjectiveTypes, b: RawObjectiveTypes) {
    // technically these arrays may not contains strings but that's probably ok
    return Array.isArray(a) && Array.isArray(b)
        ? arrayCompare(a as string[], b as string[])
        : a === b;
}
